import { forwardRef, useContext, useImperativeHandle } from 'react';
import {
  AgxRow,
  AgxButton,
  AgxColumn,
  DocumentTypes,
  CampaignDetailModel,
  AgxLabel,
  CampaignStage,
  SPECIAL_CHARACTERS,
  FormConfigType,
  AgxSparkleForm,
  AgxCaption,
  useWindowSize,
  Breakpoints,
  MultiOptionButtonOption,
  AgxBodyText,
  MarketingPackageStage,
} from '@urbanx/agx-ui-components';
import { useFeatureToggles } from 'hooks/useFeatureToggles';
import {
  requestGetFileLink,
  useGetFileLink,
} from 'components/ui-components/File/fileApi';
import { AuthContext } from '@urbanx/agx.auth.client';
import placeHolderImage from 'assets/images/placeholder-property.png';
import { beginMarketingPackage } from 'Api/Marketing/marketingApi';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useFormLoader } from 'hooks/useFormLoader';
import { MarketingPackageModel } from 'Api/Marketing/Types/Marketing';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import FileList from './FileList';
import { openFileInNewTab } from 'utils/openFileInNewTab';
import { mapPaymentStatus } from '../MarketingPackage/MarketingPackagePaymentStatusMapper';
import { getPOADocuments } from 'utils/vendorBuyerUtil';
import { formatCurrency } from 'utils/formatNumber';
import { getMarketingPackageTotal } from 'utils/marketingPackageUtil';
import './ListingDetails.scss';
import { AgxTypography } from '@urbanx/agx.ui-components2';

dayjs.extend(relativeTime);

export interface ListingDetailsHandle {
  addMarketing: () => void;
}

interface ListingDetailsProps {
  mobile?: boolean;
  campaign: CampaignDetailModel;
  marketingPackages: MarketingPackageModel[];
  setSelectedMarketingPackage: (
    marketingPackage: MarketingPackageModel
  ) => void;
}

const LISTING_DOCUMENTTYPES: DocumentTypes[] = [
  DocumentTypes.ComparativeMarketAnalysis,
  DocumentTypes.MarketingAgreement,
  DocumentTypes.TitleSearch,
  DocumentTypes.PoolCertificate,
  DocumentTypes.NoPoolCertificate,
  DocumentTypes.ProofOfIdentity,
  DocumentTypes.MaterialFact,
  DocumentTypes.PowerOfAttorney,
  DocumentTypes.Miscellaneous,
  DocumentTypes.AgencyAgreement,
  DocumentTypes.SellerDisclosure,
  DocumentTypes.SurveyPlan,
  DocumentTypes.CommunityManagementStatement,
  DocumentTypes.BodyCorporateCertificate,
];

const ListingDetails = forwardRef<ListingDetailsHandle, ListingDetailsProps>(
  (props, ref) => {
    const {
      mobile = false,
      campaign,
      marketingPackages,
      setSelectedMarketingPackage,
    } = props;

    useImperativeHandle(ref, () => ({
      addMarketing: async () => onBeginMarketingPackage(),
    }));

    const { getAuthToken } = useContext(AuthContext);
    const windowSize = useWindowSize();
    const dispatch = useAppDispatch();
    const {
      propertyImageUrls,
      documents = [],
      completionState,
      vendors,
    } = campaign;
    const { addMarketingToLiveListing } = useFeatureToggles(campaign?.state);
    const { loadForm } = useFormLoader();
    const imageUrl = propertyImageUrls?.medium ?? '';
    const isDesktop = windowSize === Breakpoints.Desktop;
    const getFileLink = useGetFileLink();

    const POADocuments = getPOADocuments(vendors);
    const combinedDocuments = [...documents, ...POADocuments];
    const filterListingDocuments = combinedDocuments
      .filter(document => LISTING_DOCUMENTTYPES.includes(document.documentType))
      .map(
        listingDocument =>
          ({
            text: listingDocument.fileName,
            documentType: listingDocument.documentType,
            onClick: async () => {
              const fileLink = await getFileLink(
                listingDocument.containerFilePath
              );
              if (fileLink !== null) {
                openFileInNewTab(isDesktop, fileLink);
              }
            },
          }) as MultiOptionButtonOption
      );

    const getAgencyAgreementLink = async () => {
      const agencyAgreementCampaign = documents.find(
        d => d.documentType === DocumentTypes.AgencyAgreement
      );

      if (!agencyAgreementCampaign) {
        return null;
      }

      const authToken = await getAuthToken();

      if (!authToken) return null;

      const link = await requestGetFileLink(
        authToken,
        agencyAgreementCampaign.containerFilePath
      );

      return link;
    };

    const onDownloadAgencyAgreementClick = async () => {
      const link = await getAgencyAgreementLink();
      if (link !== null) {
        // window.open does not work with latest safari browser
        // Workaround: wrap the window.open call to settimeout.
        // setTimeout code runs on the main thread, instead of the asynchronous one.
        setTimeout(() => {
          window.open(link, '_blank');
        });
      }
    };

    const onBeginMarketingPackage = async () => {
      const authToken = await getAuthToken();
      if (!authToken) return null;
      const { formId } = await beginMarketingPackage(
        authToken,
        dispatch,
        campaign.campaignId
      );
      await loadForm(campaign.campaignId, formId, FormConfigType.Marketing);
    };

    return (
      <AgxColumn centered veryLargeGap extraClasses="contractListingDetails">
        <AgxRow centered justifyCenter extraClasses={'listing-imageCard'}>
          <div
            style={{
              backgroundImage: `url("${imageUrl || placeHolderImage}")`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              aspectRatio: '2/1',
            }}
            className={`imageCard-image ${completionState?.completionType ? 'archived' : ''}`}
          >
            <div className="image-tag">
              <AgxLabel medium colour="brand-text-button-color">
                {
                  CampaignStage[
                    campaign.stage as unknown as keyof typeof CampaignStage
                  ].split(SPECIAL_CHARACTERS.BULLET)[1]
                }
              </AgxLabel>
            </div>
          </div>
        </AgxRow>

        {addMarketingToLiveListing && (
          <AgxColumn fill mediumGap>
            <AgxLabel medium>Marketing</AgxLabel>
            {marketingPackages.length > 0 ? (
              marketingPackages.map((marketingPackage, index) => (
                <AgxRow
                  id={marketingPackage.id}
                  datatestId={`marketingPackage-${index}`}
                  key={marketingPackage.id}
                  extraClasses={'marketingPackageListItem'}
                  largeGap
                  onClick={() => setSelectedMarketingPackage(marketingPackage)}
                >
                  <AgxColumn>
                    <AgxSparkleForm width={50} height={50} />
                  </AgxColumn>
                  <AgxColumn extraClasses={'textContainerWidth'}>
                    <AgxRow spaceBetween>
                      <AgxColumn extraClasses="marketingPackageName">
                        <AgxRow>
                          <AgxTypography
                            dataTestId={`status-${index}`}
                            id={`status-${index}`}
                            type={'caption'}
                            size={'medium'}
                            color="var(--text-tertiary)"
                          >
                            {marketingPackage.stage?.value}
                          </AgxTypography>
                        </AgxRow>
                        <AgxRow>
                          <AgxBodyText ellipsisOverflow>
                            {marketingPackage?.packageTemplateName
                              ? marketingPackage?.packageTemplateName
                              : marketingPackage?.packageDisplayName}{' '}
                            -{' '}
                            {formatCurrency(
                              getMarketingPackageTotal(marketingPackage),
                              0
                            )}
                          </AgxBodyText>
                        </AgxRow>
                      </AgxColumn>
                      <AgxColumn>
                        {marketingPackage.stage?.value ===
                          MarketingPackageStage.Complete &&
                        !!marketingPackage.displayStatus ? (
                          mapPaymentStatus(marketingPackage.displayStatus)
                        ) : (
                          <AgxCaption colour="neutral-grey-700">
                            {dayjs(marketingPackage.lastUpdated).fromNow()}
                          </AgxCaption>
                        )}
                      </AgxColumn>
                    </AgxRow>
                  </AgxColumn>
                </AgxRow>
              ))
            ) : (
              <AgxRow justifyCenter extraClasses={'noMarketingStyle'}>
                <AgxLabel>There is no marketing.</AgxLabel>
              </AgxRow>
            )}
            {!mobile && (
              <AgxButton
                primary
                id="agx-addMarketingButton"
                text={`Add Marketing`}
                medium
                wide={mobile}
                onClick={async () => onBeginMarketingPackage()}
              />
            )}
          </AgxColumn>
        )}
        <AgxColumn fill extraClasses={'fileListContainer'}>
          <FileList files={filterListingDocuments} title="Listing Documents" />
        </AgxColumn>
      </AgxColumn>
    );
  }
);

export default ListingDetails;
